// src/components/VeilleDashboard.jsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown, FaFilter, FaRedo } from 'react-icons/fa';
import { listVeilles, setVeilleActive } from '../utils/apiClient';
import ModifyVeilleModal from './ModifyVeilleModal';

const DashboardContainer = styled.div`
  background-color: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background-color: #f3f4f6;
  border-bottom: 2px solid #e5e7eb;
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  position: relative;
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  
  &.themes {
    max-width: 300px;
  }
  
  &.actions {
    width: 100px;
  }
  
  &.inactive {
    color: #9ca3af;
    font-style: italic;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #4285f4;
  cursor: pointer;
  margin-right: 10px;
  font-size: 1.2rem;
  
  &:hover {
    color: #3b78e7;
  }
  
  &.delete {
    color: #dc3545;
    
    &:hover {
      color: #c82333;
    }
  }
  
  &.redo {
    color: #28a745;
    
    &:hover {
      color: #218838;
    }
  }
`;

const SortIcon = styled.span`
  margin-left: 8px;
  display: inline-block;
`;

const FilterContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 250px;
`;

const FilterIcon = styled(FaFilter)`
  margin-right: 0.5rem;
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  background-color: ${props => props.type === 'error' ? '#f8d7da' : '#d4edda'};
  color: ${props => props.type === 'error' ? '#721c24' : '#155724'};
`;

const NoData = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`;

const VeilleDashboard = () => {
  const [veilles, setVeilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filter, setFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentVeille, setCurrentVeille] = useState(null);

  // Calcul de la différence de jours
  const daysSince = (dateStr) => {
    if (!dateStr) return 0;
    const date = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Charger les veilles depuis l'API
  const fetchVeilles = async () => {
    try {
      setLoading(true);
      const data = await listVeilles();
      // appliquer tri côté client (l'API renvoie DESC par défaut sur created_at)
      const sorted = [...data].sort((a, b) => {
        const av = a[sortField];
        const bv = b[sortField];
        if (av === bv) return 0;
        const res = av > bv ? 1 : -1;
        return sortDirection === 'asc' ? res : -res;
      });
      setVeilles(sorted);
      setError(null);
    } catch (err) {
      console.error('Erreur lors du chargement des veilles:', err);
      setError(`Erreur: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Charger les données au montage et lorsque les paramètres de tri changent
  useEffect(() => {
    fetchVeilles();
  }, [sortField, sortDirection]);

  // Fonction de tri
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Fonction de filtrage
  const filteredVeilles = veilles.filter(veille => {
    const searchTerm = filter.toLowerCase();
    return (
      veille.destinataire.toLowerCase().includes(searchTerm) ||
      veille.themes.toLowerCase().includes(searchTerm)
    );
  });

  // Gérer la désactivation d'une veille
  const handleDeactivate = async (veille) => {
    if (window.confirm('Êtes-vous sûr de vouloir désactiver cette veille ?')) {
      try {
        await setVeilleActive({ created_at: veille.created_at, destinataire: veille.destinataire, actif: false });
        
        // Rafraîchir les données
        fetchVeilles();
      } catch (err) {
        console.error('Erreur lors de la désactivation:', err);
        setError(`Erreur: ${err.message}`);
      }
    }
  };
  
  // Gérer la réactivation d'une veille
  const handleReactivate = async (veille) => {
    if (window.confirm('Voulez-vous réactiver cette veille ?')) {
      try {
        await setVeilleActive({ created_at: veille.created_at, destinataire: veille.destinataire, actif: true });
        
        // Rafraîchir les données
        fetchVeilles();
      } catch (err) {
        console.error('Erreur lors de la réactivation:', err);
        setError(`Erreur: ${err.message}`);
      }
    }
  };

  // Ouvrir le modal de modification
  const handleEdit = (veille) => {
    setCurrentVeille(veille);
    setModalOpen(true);
  };

  // Après modification ou fermeture du modal
  const handleModalClose = (updated = false) => {
    setModalOpen(false);
    setCurrentVeille(null);
    if (updated) {
      fetchVeilles();
    }
  };

  // Afficher l'icône de tri appropriée
  const renderSortIcon = (field) => {
    if (sortField !== field) return <FaSort />;
    return sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  return (
    <DashboardContainer>
      {error && <Alert type="error">{error}</Alert>}
      
      <FilterContainer>
        <FilterIcon />
        <FilterInput
          type="text"
          placeholder="Filtrer par destinataire ou thème..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </FilterContainer>
      
      {loading ? (
        <p>Chargement des veilles...</p>
      ) : filteredVeilles.length === 0 ? (
        <NoData>Aucune veille trouvée</NoData>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th sortable onClick={() => handleSort('created_at')}>
                Date de création
                <SortIcon>{renderSortIcon('created_at')}</SortIcon>
              </Th>
              <Th sortable onClick={() => handleSort('destinataire')}>
                Destinataire
                <SortIcon>{renderSortIcon('destinataire')}</SortIcon>
              </Th>
              <Th>Thèmes</Th>
              <Th sortable onClick={() => handleSort('created_at')}>
                Jours écoulés
                <SortIcon>{renderSortIcon('created_at')}</SortIcon>
              </Th>
              <Th sortable onClick={() => handleSort('actif')}>
                Statut
                <SortIcon>{renderSortIcon('actif')}</SortIcon>
              </Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredVeilles.map((veille) => (
              <tr key={veille.created_at + veille.destinataire} className={!veille.actif ? 'inactive' : ''}>
                <Td>
                  {new Date(veille.created_at).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Td>
                <Td>{veille.destinataire}</Td>
                <Td className="themes">{veille.themes}</Td>
                <Td>{daysSince(veille.created_at)} jours</Td>
                <Td>
                  {veille.actif 
                    ? <span style={{ color: 'green', fontWeight: 'bold' }}>&#10003; Actif</span> 
                    : <span style={{ color: 'red', fontWeight: 'bold' }}>&#10007; Inactif</span>}
                </Td>
                <Td className="actions">
                  {veille.actif ? (
                    <>
                      <ActionButton 
                        title="Modifier"
                        onClick={() => handleEdit(veille)}
                      >
                        <FaEdit />
                      </ActionButton>
                      <ActionButton 
                        className="delete" 
                        title="Désactiver"
                        onClick={() => handleDeactivate(veille)}
                      >
                        <FaTrash />
                      </ActionButton>
                    </>
                  ) : (
                    <ActionButton 
                      className="redo" 
                      title="Réactiver"
                      onClick={() => handleReactivate(veille)}
                    >
                      <FaRedo />
                    </ActionButton>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      
      {modalOpen && currentVeille && (
        <ModifyVeilleModal 
          veille={currentVeille}
          onClose={handleModalClose}
        />
      )}
    </DashboardContainer>
  );
};

export default VeilleDashboard;